/**
 * PlanCard Demo Component
 * Demonstrates the PlanCard component with sample data
 */

import React, { useState } from 'react';
import { Plan, Spot, Walk, Location } from '../types/core.js';
import { PlanCard } from './PlanCard.js';
import { Button } from './Button.js';

// Sample data for demonstration
const sampleStartLocation: Location = {
  lat: 35.6762,
  lng: 139.6503,
  address: '東京都渋谷区'
};

const sampleSpot1: Spot = {
  type: 'spot',
  name: 'スターバックス 渋谷店',
  genre: 'cafe',
  location: {
    lat: 35.6580,
    lng: 139.7016,
    address: '東京都渋谷区渋谷2-21-1'
  },
  duration: 45,
  openStatus: 'open',
  openingHours: 'Mo-Su 07:00-22:00',
  budget: {
    min: 500,
    max: 1500,
    currency: 'JPY'
  },
  source: 'hotpepper',
  sourceId: 'J001234567',
  tags: ['屋内', '静か', '会話向け'],
  image: {
    url: null,
    source: 'placeholder',
    attribution: null
  },
  maps: {
    placeUrl: 'https://www.google.com/maps/search/?api=1&query=スターバックス+渋谷店&query=35.6580,139.7016',
    appleUrl: 'http://maps.apple.com/?q=スターバックス+渋谷店&ll=35.6580,139.7016'
  },
  isFinal: false,
  categoryJa: 'カフェ',
  distanceM: 650,
  walkMin: 8
};

const sampleWalk1: Walk = {
  type: 'walk',
  duration: 8,
  distance: 650,
  from: sampleStartLocation,
  to: sampleSpot1.location
};

const sampleSpot2: Spot = {
  type: 'spot',
  name: '代官山 蔦屋書店',
  genre: 'bookstore',
  location: {
    lat: 35.6499,
    lng: 139.6982,
    address: '東京都渋谷区猿楽町17-5'
  },
  duration: 60,
  openStatus: 'unknown_no_data',
  openingHours: 'Mo-Su 07:00-02:00',
  budget: {
    min: 0,
    max: 3000,
    currency: 'JPY'
  },
  source: 'yolp',
  sourceId: 'Y987654321',
  tags: ['屋内', '静か', '一人向け'],
  image: {
    url: null,
    source: 'placeholder',
    attribution: null
  },
  maps: {
    placeUrl: 'https://www.google.com/maps/search/?api=1&query=代官山+蔦屋書店&query=35.6499,139.6982',
    appleUrl: 'http://maps.apple.com/?q=代官山+蔦屋書店&ll=35.6499,139.6982'
  },
  isFinal: true,
  categoryJa: '書店',
  distanceM: 950,
  walkMin: 12
};

const sampleWalk2: Walk = {
  type: 'walk',
  duration: 12,
  distance: 950,
  from: sampleSpot1.location,
  to: sampleSpot2.location
};

const sampleWalk3: Walk = {
  type: 'walk',
  duration: 10,
  distance: 800,
  from: sampleSpot2.location,
  to: sampleStartLocation
};

const samplePlan: Plan = {
  id: 'plan_demo_001',
  score: 0.85,
  estimatedDuration: 135,
  walkingTime: 30,
  budget: {
    min: 500,
    max: 4500,
    currency: 'JPY'
  },
  steps: [sampleWalk1, sampleSpot1, sampleWalk2, sampleSpot2, sampleWalk3],
  alternatives: {
    rain: {
      id: 'plan_demo_001_rain',
      score: 0.78,
      estimatedDuration: 120,
      walkingTime: 15,
      budget: {
        min: 800,
        max: 3000,
        currency: 'JPY'
      },
      steps: [
        {
          type: 'walk',
          duration: 5,
          distance: 400,
          from: sampleStartLocation,
          to: {
            lat: 35.6590,
            lng: 139.7006,
            address: '東京都渋谷区渋谷1-12-2'
          }
        },
        {
          type: 'spot',
          name: '渋谷スカイ',
          genre: 'museum',
          location: {
            lat: 35.6590,
            lng: 139.7006,
            address: '東京都渋谷区渋谷1-12-2'
          },
          duration: 90,
          openStatus: 'open',
          budget: {
            min: 1800,
            max: 1800,
            currency: 'JPY'
          },
          source: 'yolp',
          sourceId: 'Y111111111',
          tags: ['屋内', '混雑'],
          image: {
            url: null,
            source: 'placeholder',
            attribution: null
          },
          maps: {
            placeUrl: 'https://www.google.com/maps/search/?api=1&query=渋谷スカイ&query=35.6590,139.7006',
            appleUrl: 'http://maps.apple.com/?q=渋谷スカイ&ll=35.6590,139.7006'
          },
          isFinal: true,
          categoryJa: '展望台',
          distanceM: 400,
          walkMin: 5
        },
        {
          type: 'walk',
          duration: 10,
          distance: 800,
          from: {
            lat: 35.6590,
            lng: 139.7006,
            address: '東京都渋谷区渋谷1-12-2'
          },
          to: sampleStartLocation
        }
      ],
      alternatives: {},
      route: {
        links: {
          google: 'https://www.google.com/maps/dir/?api=1&origin=35.6762,139.6503&destination=35.6590,139.7006&travelmode=walking',
          apple: 'http://maps.apple.com/?saddr=35.6762,139.6503&daddr=35.6590,139.7006&dirflg=w'
        }
      },
      tags: ['屋内', '雨天対応']
    }
  },
  route: {
    links: {
      google: 'https://www.google.com/maps/dir/?api=1&origin=35.6762,139.6503&destination=35.6499,139.6982&waypoints=35.6580,139.7016&travelmode=walking',
      apple: 'http://maps.apple.com/?saddr=35.6762,139.6503&daddr=35.6499,139.6982&dirflg=w'
    }
  },
  tags: ['屋外', '会話向け', 'リラックス']
};

export const PlanCardDemo: React.FC = () => {
  const [showShareModal, setShowShareModal] = useState(false);

  const handleShare = (plan: Plan) => {
    setShowShareModal(true);
  };

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          PlanCard コンポーネント デモ
        </h2>
        <p className="text-gray-600">
          プラン表示コンポーネントの動作確認用デモです
        </p>
      </div>

      <PlanCard
        plan={samplePlan}
        planIndex={0}
        onShare={handleShare}
      />

      <div className="text-center">
        <Button
          onClick={() => setShowShareModal(true)}
          variant="outline"
        >
          共有モーダルを直接開く
        </Button>
      </div>

      {/* ShareModal は別途実装が必要 */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">プランを共有</h3>
            <p className="text-gray-600 mb-4">
              共有機能のデモです。実際の実装では共有テキストが生成されます。
            </p>
            <button
              onClick={() => setShowShareModal(false)}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
            >
              閉じる
            </button>
          </div>
        </div>
      )}

      {/* Component Information */}
      <div className="mt-8 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-medium text-gray-900 mb-2">実装された新機能</h3>
        <ul className="text-sm text-gray-700 space-y-1">
          <li>✅ 詳細な営業状況表示（不明の理由明確化）</li>
          <li>✅ 横長サムネイル画像表示（プレースホルダー対応）</li>
          <li>✅ 最終目的地の⭐️アイコン表示</li>
          <li>✅ 日本語タグのみ表示（英語フィルタリング）</li>
          <li>✅ 画像クリックでGoogleマップ連携</li>
          <li>✅ Google Places Photos APIプロキシ</li>
          <li>✅ 営業状況ツールチップ表示</li>
          <li>✅ 共有機能（LINE/Twitter/汎用）</li>
          <li>✅ 地図リンク生成（waypoints制限対応）</li>
          <li>✅ アクセシビリティ対応</li>
        </ul>
      </div>
    </div>
  );
};